import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

export default function FunnelGenerator() {
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [funnelName, setFunnelName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [customIndustry, setCustomIndustry] = useState('');
  const [customBusinessType, setCustomBusinessType] = useState('');
  const [formData, setFormData] = useState({
    businessType: '',
    customerProblem: '',
    industry: '',
    targetAudience: '',
    productService: '',
    priceRange: '',
    serviceValue: '',
    goals: '',
    budget: ''
  });
  const navigate = useNavigate();
  const { user } = useAuth();

  // 다른 페이지에서 step 4로 이동하기 위한 이벤트 리스너
  useEffect(() => {
    const handleSetStep = (event: CustomEvent) => {
      if (event.detail === 4) {
        setStep(4);
      }
    };

    window.addEventListener('setFunnelStep', handleSetStep as EventListener);
    return () => {
      window.removeEventListener('setFunnelStep', handleSetStep as EventListener);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // 업종이 "기타"가 아닌 경우 customIndustry 초기화
    if (name === 'industry' && value !== 'other') {
      setCustomIndustry('');
    }
    
    // 비즈니스 유형이 "기타"가 아닌 경우 customBusinessType 초기화
    if (name === 'businessType' && value !== 'other') {
      setCustomBusinessType('');
    }
  };

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    // 3초 후 생성 완료 시뮬레이션
    setTimeout(() => {
      setIsGenerating(false);
      setStep(4);
      // 기본 퍼널 이름 설정 - 고객 문제 기반
      setFunnelName(`${formData.customerProblem} 마케팅 퍼널`);
    }, 3000);
  };

  const handleAutoExecute = (stage: number) => {
    const routes = [
      '/awareness-campaign',
      '/interest-campaign',
      '/consideration-campaign',
      '/conversion-campaign',
      '/retention-campaign'
    ];
    navigate(routes[stage - 1]);
  };

  const handleSaveFunnel = async () => {
    if (!funnelName.trim()) {
      alert('퍼널 이름을 입력해주세요.');
      return;
    }

    if (!user) {
      // 현재 페이지 URL을 저장하고 로그인 페이지로 이동
      const currentPath = window.location.pathname;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
      return;
    }

    setIsSaving(true);

    try {
      // Supabase에 저장
      const { data, error } = await supabase
        .from('funnels')
        .insert([
          {
            user_id: user.id,
            name: funnelName.trim(),
            business_type: formData.businessType === 'other' ? customBusinessType : formData.businessType,
            industry: formData.industry === 'other' ? customIndustry : formData.industry,
            customer_problem: formData.customerProblem,
            target_audience: formData.targetAudience,
            product_service: formData.productService,
            price_range: formData.priceRange,
            service_value: formData.serviceValue,
            goals: formData.goals,
            budget: formData.budget || '미정',
            stages: {
              awareness: '소셜 미디어 광고 캠페인 (Facebook, Instagram, LinkedIn), SEO 최적화 블로그 콘텐츠 제작, 구글 애즈 및 네이버 검색 광고, 유튜브 교육 콘텐츠 시리즈를 통해 잠재 고객에게 브랜드를 알립니다.',
              interest: '간편한 회원가입 프로세스 (소셜 로그인), 무료 체험판 제공 (14일), 온보딩 튜토리얼 및 가이드, 웰컴 이메일 및 첫 사용 혜택을 통해 고객의 관심을 유도합니다.',
              consideration: '정기적인 제품 업데이트 및 신기능 알림, 개인화된 이메일 마케팅, 고객 성공 팀의 정기 체크인, 사용 팁 및 베스트 프랙티스 공유를 통해 고객이 제품을 깊이 고려하도록 합니다.',
              conversion: '프리미엄 플랜 업그레이드 제안, 크로스셀 및 업셀 전략, 연간 구독 할인 프로모션, 부가 서비스 및 컨설팅 제공을 통해 실제 구매로 전환시킵니다.',
              retention: '추천인 보상 프로그램 (양방향 혜택), 소셜 공유 기능 및 인센티브, 고객 후기 및 사례 연구 요청, 앰버서더 프로그램 운영을 통해 장기 고객으로 유지합니다.'
            }
          }
        ])
        .select();

      if (error) {
        console.error('Supabase 오류:', error);
        throw new Error(error.message);
      }

      setIsSaving(false);
      
      // 저장 성공 알림
      alert('퍼널이 성공적으로 저장되었습니다!');
      
      // 저장된 퍼널 페이지로 이동
      navigate('/my-funnels');
    } catch (error: any) {
      console.error('퍼널 저장 실패:', error);
      setIsSaving(false);
      
      // 더 구체적인 오류 메시지
      let errorMessage = '퍼널 저장에 실패했습니다.';
      
      if (error.message?.includes('permission') || error.message?.includes('policy')) {
        errorMessage = '저장 권한이 없습니다. 다시 로그인해주세요.';
        // 로그인 페이지로 리다이렉트
        setTimeout(() => {
          const currentPath = window.location.pathname;
          navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
        }, 2000);
      } else if (error.message?.includes('network')) {
        errorMessage = '네트워크 연결을 확인해주세요.';
      } else if (error.message) {
        errorMessage = `저장 실패: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  const handleRegenerateFunnel = async () => {
    if (!user) {
      const currentPath = window.location.pathname;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
      return;
    }

    try {
      // 현재 퍼널을 자동으로 저장
      await supabase
        .from('funnels')
        .insert([
          {
            user_id: user.id,
            name: funnelName.trim() || `${formData.customerProblem} 마케팅 퍼널`,
            business_type: formData.businessType === 'other' ? customBusinessType : formData.businessType,
            industry: formData.industry === 'other' ? customIndustry : formData.industry,
            customer_problem: formData.customerProblem,
            target_audience: formData.targetAudience,
            product_service: formData.productService,
            price_range: formData.priceRange,
            service_value: formData.serviceValue,
            goals: formData.goals,
            budget: formData.budget || '미정',
            stages: {
              awareness: '소셜 미디어 광고 캠페인 (Facebook, Instagram, LinkedIn), SEO 최적화 블로그 콘텐츠 제작, 구글 애즈 및 네이버 검색 광고, 유튜브 교육 콘텐츠 시리즈를 통해 잠재 고객에게 브랜드를 알립니다.',
              interest: '간편한 회원가입 프로세스 (소셜 로그인), 무료 체험판 제공 (14일), 온보딩 튜토리얼 및 가이드, 웰컴 이메일 및 첫 사용 혜택을 통해 고객의 관심을 유도합니다.',
              consideration: '정기적인 제품 업데이트 및 신기능 알림, 개인화된 이메일 마케팅, 고객 성공 팀의 정기 체크인, 사용 팁 및 베스트 프랙티스 공유를 통해 고객이 제품을 깊이 고려하도록 합니다.',
              conversion: '프리미엄 플랜 업그레이드 제안, 크로스셀 및 업셀 전략, 연간 구독 할인 프로모션, 부가 서비스 및 컨설팅 제공을 통해 실제 구매로 전환시킵니다.',
              retention: '추천인 보상 프로그램 (양방향 혜택), 소셜 공유 기능 및 인센티브, 고객 후기 및 사례 연구 요청, 앰버서더 프로그램 운영을 통해 장기 고객으로 유지합니다.'
            }
          }
        ]);
      
      // 동일한 비즈니스 정보로 새로운 퍼널 생성
      setStep(3);
      
      // 잠시 후 자동으로 생성 시작
      setTimeout(() => {
        handleGenerate();
      }, 500);
    } catch (error: any) {
      console.error('퍼널 저장 실패:', error);
      if (error.message?.includes('permission') || error.message?.includes('policy')) {
        alert('저장 권한이 없습니다. 다시 로그인해주세요.');
        const currentPath = window.location.pathname;
        navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50" style={{ fontFamily: '"Noto Sans KR", sans-serif' }}>
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 진행 상태 표시 */}
          <div className="mb-12">
            <div className="flex items-center">
              {[
                { num: 1, label: '비즈니스 정보' },
                { num: 2, label: '타겟 및 목표' },
                { num: 3, label: '퍼널 생성' }
              ].map((item, index) => (
                <div key={item.num} className="flex items-center flex-1">
                  <div className="flex flex-col items-center w-full">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      step >= item.num ? 'bg-teal-600 text-white' : 'bg-gray-300 text-gray-600'
                    }`}>
                      {item.num}
                    </div>
                    <div className="mt-2 text-sm font-medium text-gray-700 text-center whitespace-nowrap">
                      {item.label}
                    </div>
                  </div>
                  {index < 2 && (
                    <div className={`h-1 flex-1 ${
                      step > item.num ? 'bg-teal-600' : 'bg-gray-300'
                    }`} style={{ marginTop: '-24px' }}></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step 1: 비즈니스 정보 */}
          {step === 1 && (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">비즈니스 정보를 입력해주세요</h2>
              <p className="text-gray-600 mb-8">AI가 귀하의 비즈니스를 분석하여 최적의 마케팅 퍼널을 생성합니다</p>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    업종 *
                  </label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                  >
                    <option value="">업종을 선택하세요</option>
                    <option value="ecommerce">이커머스</option>
                    <option value="saas">SaaS</option>
                    <option value="education">교육</option>
                    <option value="consultinging">컨설팅</option>
                    <option value="healthcare">헬스케어</option>
                    <option value="finance">금융</option>
                    <option value="realestate">부동산</option>
                    <option value="other">기타</option>
                  </select>
                </div>

                {formData.industry === 'other' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      업종 직접 입력 *
                    </label>
                    <input
                      type="text"
                      value={customIndustry}
                      onChange={(e) => setCustomIndustry(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="예: 뷰티, 식품, 제조업 등"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    비즈니스 유형 *
                  </label>
                  <select
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                  >
                    <option value="">비즈니스 유형을 선택하세요</option>
                    <option value="product">제품 판매 (유형 제품)</option>
                    <option value="service">서비스 제공</option>
                    <option value="product-service">제품 + 서비스</option>
                    <option value="digital">디지털 콘텐츠</option>
                    <option value="subscription">구독 서비스</option>
                    <option value="platform">플랫폼/마켓플레이스</option>
                    <option value="other">기타</option>
                  </select>
                </div>

                {formData.businessType === 'other' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      비즈니스 유형 직접 입력 *
                    </label>
                    <input
                      type="text"
                      value={customBusinessType}
                      onChange={(e) => setCustomBusinessType(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="예: 프랜차이즈, 도소매, 제조 등"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    고객이 해결하고 싶은 문제 *
                  </label>
                  <input
                    type="text"
                    name="customerProblem"
                    value={formData.customerProblem}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="예: 시간 절약, 비용 절감, 업무 효율성 향상, 건강 개선 등"
                  />
                  <p className="text-xs text-gray-500 mt-1">이 제품/서비스를 이용하는 고객은 어떤 문제를 해결하고 싶어하나요?</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    제품/서비스 가격대 *
                  </label>
                  <select
                    name="priceRange"
                    value={formData.priceRange}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                  >
                    <option value="">가격대를 선택하세요</option>
                    <option value="low">저가형</option>
                    <option value="mid">중가형</option>
                    <option value="high">고가형</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    서비스 가치 (최대 효과) *
                  </label>
                  <textarea
                    name="serviceValue"
                    value={formData.serviceValue}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="예: 업무 시간 50% 단축, 매출 30% 증가, 고객 만족도 향상 등"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">이 제품/서비스를 사용 시 얻는 최대 효과는 무엇인가요?</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    제품/서비스 차별점 *
                  </label>
                  <textarea
                    name="productService"
                    value={formData.productService}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="제공하는 제품이나 서비스의 특징, 차별점, 주요 기능 등을 구체적으로 설명해주세요"
                  ></textarea>
                </div>
              </div>

              <div className="flex justify-end mt-8">
                <button
                  onClick={handleNext}
                  disabled={
                    !formData.businessType || 
                    (formData.businessType === 'other' && !customBusinessType.trim()) ||
                    !formData.customerProblem || 
                    !formData.industry || 
                    (formData.industry === 'other' && !customIndustry.trim()) ||
                    !formData.priceRange ||
                    !formData.serviceValue ||
                    !formData.productService
                  }
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-lg font-semibold transition-all disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  다음 단계
                </button>
              </div>
            </div>
          )}

          {/* Step 2: 타겟 및 목표 */}
          {step === 2 && (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">타겟 고객과 목표를 설정하세요</h2>
              <p className="text-gray-600 mb-8">더 정확한 퍼널 생성을 위해 타겟 고객과 마케팅 목표를 알려주세요</p>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    타겟 고객 *
                  </label>
                  <textarea
                    name="targetAudience"
                    value={formData.targetAudience}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="예: 30-40대 중소기업 마케팅 담당자"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    마케팅 목표 *
                  </label>
                  <textarea
                    name="goals"
                    value={formData.goals}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="예: 월 100명의 신규 고객 확보, 전환율 5% 달성"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    월 마케팅 예산
                  </label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 cursor-pointer"
                  >
                    <option value="">예산 범위를 선택하세요</option>
                    <option value="under100">100만원 미만</option>
                    <option value="100-300">100-300만원</option>
                    <option value="300-500">300-500만원</option>
                    <option value="500-1000">500-1000만원</option>
                    <option value="over1000">1000만원 이상</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <button
                  onClick={handlePrevious}
                  className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
                >
                  이전
                </button>
                <button
                  onClick={handleNext}
                  disabled={!formData.targetAudience || !formData.goals}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-lg font-semibold transition-all disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  다음 단계
                </button>
              </div>
            </div>
          )}

          {/* Step 3: 퍼널 생성 */}
          {step === 3 && !isGenerating && (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">입력 정보 확인</h2>
              <p className="text-gray-600 mb-8">입력하신 정보를 확인하고 AI 퍼널 생성을 시작하세요</p>
              
              <div className="space-y-4 bg-gray-50 rounded-lg p-6 mb-8">
                <div className="border-b border-gray-200 pb-3">
                  <span className="text-sm font-semibold text-gray-600">업종</span>
                  <p className="text-gray-900 mt-1">{formData.industry === 'other' ? customIndustry : formData.industry}</p>
                </div>
                <div className="border-b border-gray-200 pb-3">
                  <span className="text-sm font-semibold text-gray-600">비즈니스 유형</span>
                  <p className="text-gray-900 mt-1">{formData.businessType === 'other' ? customBusinessType : formData.businessType}</p>
                </div>
                <div className="border-b border-gray-200 pb-3">
                  <span className="text-sm font-semibold text-gray-600">고객이 해결하고 싶은 문제</span>
                  <p className="text-gray-900 mt-1">{formData.customerProblem}</p>
                </div>
                <div className="border-b border-gray-200 pb-3">
                  <span className="text-sm font-semibold text-gray-600">가격대</span>
                  <p className="text-gray-900 mt-1">{formData.priceRange}</p>
                </div>
                <div className="border-b border-gray-200 pb-3">
                  <span className="text-sm font-semibold text-gray-600">서비스 가치 (최대 효과)</span>
                  <p className="text-gray-900 mt-1">{formData.serviceValue}</p>
                </div>
                <div className="border-b border-gray-200 pb-3">
                  <span className="text-sm font-semibold text-gray-600">제품/서비스</span>
                  <p className="text-gray-900 mt-1">{formData.productService}</p>
                </div>
                <div className="border-b border-gray-200 pb-3">
                  <span className="text-sm font-semibold text-gray-600">타겟 고객</span>
                  <p className="text-gray-900 mt-1">{formData.targetAudience}</p>
                </div>
                <div className="border-b border-gray-200 pb-3">
                  <span className="text-sm font-semibold text-gray-600">마케팅 목표</span>
                  <p className="text-gray-900 mt-1">{formData.goals}</p>
                </div>
                {formData.budget && (
                  <div>
                    <span className="text-sm font-semibold text-gray-600">월 마케팅 예산</span>
                    <p className="text-gray-900 mt-1">{formData.budget}</p>
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={handlePrevious}
                  className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
                >
                  이전
                </button>
                <button
                  onClick={handleGenerate}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-lg font-semibold transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  AI 퍼널 생성하기
                </button>
              </div>
            </div>
          )}

          {/* 생성 중 */}
          {isGenerating && (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                <i className="ri-cpu-line text-4xl text-teal-600"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">AI가 퍼널을 생성하고 있습니다</h2>
              <p className="text-gray-600 mb-8">비즈니스 정보를 분석하여 최적의 마케팅 퍼널을 설계하고 있습니다...</p>
              
              <div className="max-w-md mx-auto">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-teal-600 rounded-full animate-[loading_2s_ease-in-out_infinite]"></div>
                </div>
              </div>

              <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                <div className="flex items-start space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-900">비즈니스 분석</h4>
                    <p className="text-sm text-gray-600">업종 및 타겟 분석 완료</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-loader-4-line text-teal-600 text-xl mt-1 animate-spin"></i>
                  <div>
                    <h4 className="font-semibold text-gray-900">퍼널 설계</h4>
                    <p className="text-sm text-gray-600">최적 경로 생성 중...</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-time-line text-gray-400 text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-gray-400">전략 최적화</h4>
                    <p className="text-sm text-gray-400">대기 중...</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: 생성 완료 */}
          {step === 4 && (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-line text-4xl text-teal-600"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">퍼널 생성이 완료되었습니다!</h2>
                <p className="text-gray-600 mb-8">AI가 분석한 최적의 AARRR 마케팅 퍼널을 확인하세요</p>
              </div>

              {/* 퍼널 이름 입력 */}
              <div className="max-w-2xl mx-auto mb-8">
                <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 border-2 border-teal-200">
                  <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <i className="ri-bookmark-line text-teal-600"></i>
                    퍼널 이름
                  </label>
                  <input
                    type="text"
                    value={funnelName}
                    onChange={(e) => setFunnelName(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-teal-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-base font-medium"
                    placeholder="예: 2024 Q1 마케팅 퍼널"
                  />
                  <p className="text-xs text-gray-600 mt-2">
                    <i className="ri-information-line"></i> 나중에 쉽게 찾을 수 있도록 퍼널 이름을 입력하세요
                  </p>
                </div>
              </div>

              {/* 깔대기 시각화 */}
              <div className="mb-12 max-w-2xl mx-auto">
                <div className="relative">
                  {/* 1단계 - 획득 (가장 넓음) */}
                  <div className="relative mb-2">
                    <div className="bg-gradient-to-r from-teal-400 to-teal-500 rounded-t-lg p-4 text-white text-center shadow-lg">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="bg-white text-teal-600 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                        <h4 className="font-bold text-lg">획득 단계</h4>
                      </div>
                      <p className="text-sm text-teal-50">Acquisition</p>
                    </div>
                  </div>

                  {/* 2단계 - 활성화 */}
                  <div className="relative mb-2 mx-8">
                    <div className="bg-gradient-to-r from-blue-400 to-blue-500 p-4 text-white text-center shadow-lg">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="bg-white text-blue-600 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                        <h4 className="font-bold text-lg">활성화 단계</h4>
                      </div>
                      <p className="text-sm text-blue-50">Activation</p>
                    </div>
                  </div>

                  {/* 3단계 - 유지 */}
                  <div className="relative mb-2 mx-16">
                    <div className="bg-gradient-to-r from-purple-400 to-purple-500 p-4 text-white text-center shadow-lg">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="bg-white text-purple-600 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                        <h4 className="font-bold text-lg">유지 단계</h4>
                      </div>
                      <p className="text-sm text-purple-50">Retention</p>
                    </div>
                  </div>

                  {/* 4단계 - 추천 */}
                  <div className="relative mb-2 mx-24">
                    <div className="bg-gradient-to-r from-orange-400 to-orange-500 p-4 text-white text-center shadow-lg">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="bg-white text-orange-600 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">4</span>
                        <h4 className="font-bold text-lg">추천 단계</h4>
                      </div>
                      <p className="text-sm text-orange-50">Referral</p>
                    </div>
                  </div>

                  {/* 5단계 - 수익 (가장 좁음) */}
                  <div className="relative mx-32">
                    <div className="bg-gradient-to-r from-green-400 to-green-500 rounded-b-lg p-4 text-white text-center shadow-lg">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="bg-white text-green-600 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">5</span>
                        <h4 className="font-bold text-lg">수익 단계</h4>
                      </div>
                      <p className="text-sm text-green-50">Revenue</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="border-2 border-teal-200 rounded-lg p-6 bg-teal-50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <span className="text-5xl font-bold text-teal-600">1</span>
                      <h3 className="text-xl font-bold text-gray-900">획득 단계 (Acquisition)</h3>
                    </div>
                    <button
                      onClick={() => handleAutoExecute(1)}
                      className="bg-teal-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-play-circle-line"></i>
                      자동 실행
                    </button>
                  </div>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                      <span className="text-gray-700">소셜 미디어 광고 캠페인 (Facebook, Instagram, LinkedIn)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                      <span className="text-gray-700">SEO 최적화 블로그 콘텐츠 제작</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                      <span className="text-gray-700">구글 애즈 및 네이버 검색 광고</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                      <span className="text-gray-700">유튜브 교육 콘텐츠 시리즈</span>
                    </li>
                  </ul>
                </div>

                <div className="border-2 border-blue-200 rounded-lg p-6 bg-blue-50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <span className="text-5xl font-bold text-blue-600">2</span>
                      <h3 className="text-xl font-bold text-gray-900">활성화 단계 (Activation)</h3>
                    </div>
                    <button
                      onClick={() => handleAutoExecute(2)}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-play-circle-line"></i>
                      자동 실행
                    </button>
                  </div>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-blue-600 mt-1"></i>
                      <span className="text-gray-700">간편한 회원가입 프로세스 (소셜 로그인)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-blue-600 mt-1"></i>
                      <span className="text-gray-700">무료 체험판 제공 (14일)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-blue-600 mt-1"></i>
                      <span className="text-gray-700">온보딩 튜토리얼 및 가이드</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-blue-600 mt-1"></i>
                      <span className="text-gray-700">웰컴 이메일 및 첫 사용 혜택</span>
                    </li>
                  </ul>
                </div>

                <div className="border-2 border-purple-200 rounded-lg p-6 bg-purple-50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <span className="text-5xl font-bold text-purple-600">3</span>
                      <h3 className="text-xl font-bold text-gray-900">유지 단계 (Retention)</h3>
                    </div>
                    <button
                      onClick={() => handleAutoExecute(3)}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-play-circle-line"></i>
                      자동 실행
                    </button>
                  </div>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-purple-600 mt-1"></i>
                      <span className="text-gray-700">정기적인 제품 업데이트 및 신기능 알림</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-purple-600 mt-1"></i>
                      <span className="text-gray-700">개인화된 이메일 마케팅</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-purple-600 mt-1"></i>
                      <span className="text-gray-700">고객 성공 팀의 정기 체크인</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-purple-600 mt-1"></i>
                      <span className="text-gray-700">사용 팁 및 베스트 프랙티스 공유</span>
                    </li>
                  </ul>
                </div>

                <div className="border-2 border-orange-200 rounded-lg p-6 bg-orange-50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <span className="text-5xl font-bold text-orange-600">4</span>
                      <h3 className="text-xl font-bold text-gray-900">추천 단계 (Referral)</h3>
                    </div>
                    <button
                      onClick={() => handleAutoExecute(4)}
                      className="bg-orange-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-play-circle-line"></i>
                      자동 실행
                    </button>
                  </div>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-orange-600 mt-1"></i>
                      <span className="text-gray-700">추천인 보상 프로그램 (양방향 혜택)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-orange-600 mt-1"></i>
                      <span className="text-gray-700">소셜 공유 기능 및 인센티브</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-orange-600 mt-1"></i>
                      <span className="text-gray-700">고객 후기 및 사례 연구 요청</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-orange-600 mt-1"></i>
                      <span className="text-gray-700">앰버서더 프로그램 운영</span>
                    </li>
                  </ul>
                </div>

                <div className="border-2 border-green-200 rounded-lg p-6 bg-green-50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <span className="text-5xl font-bold text-green-600">5</span>
                      <h3 className="text-xl font-bold text-gray-900">수익 단계 (Revenue)</h3>
                    </div>
                    <button
                      onClick={() => handleAutoExecute(5)}
                      className="bg-green-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-play-circle-line"></i>
                      자동 실행
                    </button>
                  </div>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                      <span className="text-gray-700">프리미엄 플랜 업그레이드 제안</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                      <span className="text-gray-700">크로스셀 및 업셀 전략</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                      <span className="text-gray-700">연간 구독 할인 프로모션</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                      <span className="text-gray-700">부가 서비스 및 컨설팅 제공</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-teal-50 border-l-4 border-teal-600 p-6 mb-8">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-lightbulb-line text-teal-600 mr-2"></i>
                  AI 추천 사항
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• 획득 단계에서는 다양한 채널을 테스트하여 가장 효과적인 채널을 찾으세요</li>
                  <li>• 활성화 단계의 온보딩 경험을 최적화하여 첫 가치 경험 시간을 단축하세요</li>
                  <li>• 유지 단계에서는 사용자 행동 데이터를 분석하여 이탈 징후를 조기에 발견하세요</li>
                  <li>• 추천 프로그램은 만족도가 높은 고객을 대상으로 먼저 시작하세요</li>
                  <li>• 수익 단계에서는 고객 생애 가치(LTV)를 지속적으로 모니터링하세요</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={handleSaveFunnel}
                  disabled={isSaving}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-lg font-semibold transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none animate-pulse shadow-2xl"
                >
                  {isSaving ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      저장 중...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line"></i>
                      퍼널 저장하기
                    </>
                  )}
                </button>
                <button 
                  onClick={handleRegenerateFunnel}
                  disabled={isSaving}
                  className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  퍼널 다시 생성하기
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />

      <style>{`
        @keyframes loading {
          0% { width: 0%; }
          50% { width: 70%; }
          100% { width: 100%; }
        }
      `}</style>
    </div>
  );
}
